import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { BranchTableCodesComponent } from './admintool/piping/dictionaries/branch-table-codes/branch-table-codes.component';
import { BranchComponent } from './admintool/piping/dictionaries/branch/branch.component';
import { CommonnotesComponent } from './admintool/piping/dictionaries/common-notes/common-notes.component';
import { ComponentCatalogComponent } from './admintool/piping/dictionaries/component-catalog/component-catalog.component';
import { ComponentPropertiesComponent } from './admintool/piping/dictionaries/component-properties/component-properties.component';
import { ApplicationsComponent } from './admintool/piping/dictionaries/applications/applications.component';
import { BusinessUnitsComponent } from './admintool/piping/dictionaries/business-units/business-units.component';
import { SpecificationsComponent } from './admintool/piping/dictionaries/specifications/specifications.component';
import { PipeClassSummaryPropertiesComponent } from './admintool/piping/dictionaries/pipe-class-summary-properties/pipe-class-summary-properties.component';
import { DictionariesComponent } from './admintool/piping/dictionaries/dictionaries.component';
import { PressuretemperatureComponent } from './admintool/piping/dictionaries/pressure-temperature/pressure-temperature.component';
import { ServiceDetailsComponent } from './admintool/piping/dictionaries/service-details/service-details.component';
import { ServicesComponent } from './admintool/piping/dictionaries/services/services.component';
import { MsalComponent } from './core/components/msal.component';
import { pipingDataReadViewGuard, pipingRevisionEditViewGuard, pipingRevisionReadViewGuard, pipingValvesBulkUploadGuard, userManagementAdministrationGuard, buPdfBulkUploadGuard } from './core/guards/guards';
import { unsavedChangesGuard } from './core/guards/unsaved-changes.guard';
import { ContactUsComponent } from './help/contact-us.component';
import { MyWorkspaceComponent } from './my-workspace/pages/my-workspace/my-workspace.component';
import { Esra1CompatibilityComponent } from './piping-valves/esra1-compatibility/esra1-compatibility.component';
import { ReleasesAndRevisionsComponent } from './piping-valves/piping-releases-and-revisions/piping-releases-and-revisions.component';
import { RevisionsTabComponent } from './piping-valves/piping-releases-and-revisions/revisions-tab/revisions-tab.component';
import { ReleasesTabComponent } from './piping-valves/piping-releases-and-revisions/releases-tab/releases-tab.component';
import { ReleaseDetailsComponent } from './piping-valves/piping-releases-and-revisions/release-details/release-details.component';
import { ReleaseFinalChecksComponent } from './piping-valves/release-final-checks/release-final-checks.component';
import { PressureTempTableComponent } from './piping-valves/pressure-temp-table/pressure-temp-table.component';
import { EditCoatingProductComponent } from './revisions/dialogues/edit-coating-product/edit-coating-product.component';
import { EditCoatingSystemComponent } from './revisions/dialogues/edit-coating-system/edit-coating-system.component';
import { AllRevisionsComponent } from './revisions/pages/all-revisions/all-revisions.component';
import { AllWeldingRevisionsComponent } from './revisions/pages/all-welding-revisions/all-welding-revisions.component';
import { CoatingsRevisionSummaryComponent } from './revisions/pages/coatings-revision-summary/coatings-revision-summary.component';
import { UpdateCoatingSolutionComponent } from './revisions/pages/update-coating-solution/update-coating-solution.component';
import { WeldingRevisionSummaryComponent } from './revisions/pages/welding-revision-summary/welding-revision-summary.component';
import { SpecExportComponent } from './revisions/spec-export/spec-export.component';
import { HomeComponent } from './sample/home/home.component';
import { NotAuthorizedComponent } from './sample/not-authorized/not-authorized.component';
import { ShareLinkResolverComponent } from './sample/share-link-resolver/share-link-resolver.component';
import { CoatingSolutionSearchComponent } from './search/pages/coating-solution-search/coating-solution-search.component';
import { PipingAdminSearchComponent } from './search/pages/piping-admin-search/piping-admin-search.component';
import { PipingSearchComponent } from './search/pages/piping-search/piping-search.component';
import { WeldingSearchComponent } from './search/pages/welding-search/welding-search.component';
import { PipingTabsParentComponent } from './search/piping-tabs-parent/piping-tabs-parent.component';
import { ValveSummaryPageComponent } from './search/valve-summary-page/valve-summary-page.component';
import { ValveEditPageComponent } from './search/valve-edit-page/valve-edit-page.component';
import { ValveBulkUploadComponent } from './search/valve-bulk-upload/valve-bulk-upload.component';
import { BuPdfBulkUploadComponent } from './piping-valves/bu-pdf-bulk-upload/bu-pdf-bulk-upload.component';
import { ConsolidationMappingComponent } from './admintool/administration/consolidation-mapping/consolidation-mapping.component';
import { UserManagementListComponent } from './admintool/administration/user-management/user-management-list.component';
import { UserManagementDetailComponent } from './admintool/administration/user-management/user-management-detail.component';

const routes: Routes = [
  {
    // Needed for hash routing
    path: 'code',
    component: MsalComponent,
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [MsalGuard],
  },
  {
    path: '403',
    component: NotAuthorizedComponent,
    canActivate: [MsalGuard],
  },
  {
    // Pipe class permalink, e.g. /p/CES/12DF01 -- resolved via ShareLinkResolve/resolve.
    path: 'p/:businessUnit/:name',
    component: ShareLinkResolverComponent,
    canActivate: [MsalGuard],
  },
  {
    // Valve permalink, e.g. /v/CES/AGCASE1_ON_CES_F -- resolved via ShareLinkResolve/resolve.
    path: 'v/:businessUnit/:name',
    component: ShareLinkResolverComponent,
    canActivate: [MsalGuard],
  },
  {
    // ESRA1 pipe class PDF permalink, e.g. /pp/CES/12DF01 -- resolved via ShareLinkResolve/resolve.
    path: 'pp/:businessUnit/:name',
    component: ShareLinkResolverComponent,
    canActivate: [MsalGuard],
  },
  {
    // ESRA1 valve PDF permalink, e.g. /pv/CES/AGCASE1_ON_CES_F -- resolved via ShareLinkResolve/resolve.
    path: 'pv/:businessUnit/:name',
    component: ShareLinkResolverComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'coatingsolutionsearch',
    component: CoatingSolutionSearchComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'weldingsearch',
    component: WeldingSearchComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'myworkspace',
    component: MyWorkspaceComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'spec-export',
    component: SpecExportComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'contactus',
    component: ContactUsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'coatingsrevision/:id',
    component: CoatingsRevisionSummaryComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'allrevisions',
    component: AllRevisionsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'updatecoatingsolution/:id/:revisionid',
    component: UpdateCoatingSolutionComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'coatingsystem/:coatingsystemid/:revisionid',
    component: EditCoatingSystemComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'coatingproduct/:coatingproductid/:revisionid',
    component: EditCoatingProductComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'weldingrevision/all',
    component: AllWeldingRevisionsComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'weldingrevision/:id',
    component: WeldingRevisionSummaryComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'weldingrevision/:id',
    component: WeldingRevisionSummaryComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'piping/dictionaries',
    component: DictionariesComponent,
    canActivate: [MsalGuard, pipingDataReadViewGuard()],
    children: [
      {
        path: 'commonnotes',
        component: CommonnotesComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'services',
        component: ServicesComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'servicedetails',
        component: ServiceDetailsComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'pressureandtemperature',
        component: PressuretemperatureComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'branch',
        component: BranchComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'branchTableCodes',
        component: BranchTableCodesComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'componentcatalog',
        component: ComponentCatalogComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'componentproperties',
        component: ComponentPropertiesComponent,
        canActivate: [MsalGuard],
        data: {
          fetchNonValves: true,
          fetchValves: false,
        },
      },
      {
        path: 'valveproperties',
        component: ComponentPropertiesComponent,
        canActivate: [MsalGuard],
        data: {
          fetchNonValves: false,
          fetchValves: true,
        },
      },
      {
        path: 'applications',
        component: ApplicationsComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'businessunits',
        component: BusinessUnitsComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'specifications',
        component: SpecificationsComponent,
        canActivate: [MsalGuard]
      },
      {
        path: 'pipeclassproperties',
        component: PipeClassSummaryPropertiesComponent,
        canActivate: [MsalGuard]
      },
    ],
  },
  {
    // Read access follows the standard authenticated-user pattern; create/update/delete
    // actions inside the component are additionally gated on Admin or Piping.Dictionary.Write.
    path: 'administration/consolidation-mapping',
    component: ConsolidationMappingComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'administration/user-management',
    component: UserManagementListComponent,
    canActivate: [MsalGuard, userManagementAdministrationGuard()],
  },
  {
    path: 'administration/user-management/:id',
    component: UserManagementDetailComponent,
    canActivate: [MsalGuard, userManagementAdministrationGuard()],
  },
  {
    path: 'piping/search',
    component: PipingSearchComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'pipingsummary/:id',
    component: PipingTabsParentComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'pipingsummary/:businessUnit/:name',
    component: PipingTabsParentComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'piping/pipeClass/:name/:isEditMode',
    component: PipingTabsParentComponent,
    canActivate: [MsalGuard, pipingRevisionEditViewGuard()],
  },
  {
    path: 'pressuretemperaturetable',
    component: PressureTempTableComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'valvesummary/:id',
    component: ValveSummaryPageComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'piping/valve/edit',
    component: ValveEditPageComponent,
    canActivate: [MsalGuard, pipingRevisionEditViewGuard()],
    canDeactivate: [unsavedChangesGuard],
  },
  {
    path: 'piping/revisionsAndReleases',
    component: ReleasesAndRevisionsComponent,
    canActivate: [MsalGuard, pipingRevisionReadViewGuard()],
    children: [
      { path: '', redirectTo: 'revisions', pathMatch: 'full' },
      { path: 'revisions', component: RevisionsTabComponent },
      { path: 'releases', component: ReleasesTabComponent },
    ]
  },
  {
    path: 'piping/releases/:releaseId',
    component: ReleaseDetailsComponent,
    canActivate: [MsalGuard, pipingRevisionReadViewGuard()],
  },
  {
    path: 'piping/releases/:releaseId/final-checks',
    component: ReleaseFinalChecksComponent,
    canActivate: [MsalGuard, pipingRevisionEditViewGuard()],
  },
  {
    path: 'piping/adminSearch',
    component: PipingAdminSearchComponent,
    canActivate: [MsalGuard, pipingDataReadViewGuard()],
  },
  {
    path: 'view/:pipeclassNameAndBusinessUnit',
    pathMatch: 'prefix',
    component: Esra1CompatibilityComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'view/:pipeclassNameAndBusinessUnit/:trailingUnusedLegacyParameter',
    pathMatch: 'prefix',
    component: Esra1CompatibilityComponent,
    canActivate: [MsalGuard],
  },
  {
    path: 'piping/valveBulkUpload',
    component: ValveBulkUploadComponent,
    canActivate: [MsalGuard, pipingValvesBulkUploadGuard()],
  },
  {
    path: 'piping/buPdfBulkUpload',
    component: BuPdfBulkUploadComponent,
    canActivate: [MsalGuard, buPdfBulkUploadGuard()],
    canDeactivate: [unsavedChangesGuard],
  },
  {
    path: '**',
    component: HomeComponent,
    canActivate: [MsalGuard],
  },
];

const isIframe = window !== window.parent && !window.opener; // Remove this line to use Angular Universal

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      useHash: false,
      // Don't perform initial navigation in iframes
      initialNavigation: !isIframe ? 'enabledBlocking' : 'disabled', // Remove this line to use Angular Universal
    }),
  ],
  //   imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
